import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { withSize } from 'react-sizeme';
import { observer } from 'mobx-react-lite';

import {
    QuestionCompact,
    PrimaryButton,
    useForm,
    Sizes,
    ANALYTICS_KEY,
    ANALYTICS_ATTRIBUTE_NAME,
} from '@ratehub/base-ui';
import {
    RATE_TYPES,
} from '@ratehub/documents/mtg-journey-document/stable';

import isProviderRate from '../functions/isProviderRate';
import FormattedPrimeRateMessage from './FormattedPrimeRateMessage';
import RateLogo from './RateLogo';


function MortgageRateSelector({
    rate,
    className,
    onSelectRate,
    size,
}) {
    const form = useForm();
    const customRateField = form.getFieldNamed('customRate');

    const isDesktop = size.width >= MOBILE_WIDTH;

    return (
        <Container
            ref={self => self && (self[ANALYTICS_KEY] = rate)}
            className={classNames(className, 'rh-display-flex rh-box-sizing-border-box', { 
                [ANALYTICS_ATTRIBUTE_NAME]: !!rate,
                'rh-p-0_5': !isDesktop,
                'rh-py-0_75 rh-px-2': isDesktop,
            })}
            data-test-name="mortgage-rate-selector"
        >
            <Choose>
                <When condition={isProviderRate(rate)}>
                    <div className="rh-display-flex column-1">
                        <p
                            className={classNames('rate rh-fg-blackberry rh-m-0', {
                                'rh-title-2xs': !isDesktop,
                                'rh-title-m': isDesktop,
                            })}
                        >
                            <FormattedNumber
                                value={rate?.value / 100}
                                format="percent2"
                            />
                        </p>

                        <If condition={rate.type === RATE_TYPES.VARIABLE}>
                            <p
                                className={classNames('rh-fg-blackberry rh-m-0 weight-medium rh-no-wrap', {
                                    'rh-text-2xs': !isDesktop,
                                    'rh-text-xs': isDesktop,
                                })}
                            >
                                <FormattedPrimeRateMessage
                                    {...rate}
                                />
                            </p>
                        </If>
                    </div>

                    <RateLogo
                        {...rate}
                        className="column-2"
                    />
                </When>
                <Otherwise>
                    <If condition={isDesktop}>
                        <p className="rh-fg-blackberry rh-text-s column-1">
                            <FormattedMessage
                                id="mtg-common.MortgageRateSelector.customRateLabel"
                                defaultMessage="Use custom rate"
                            />
                        </p>
                    </If>
                    <div className={classNames('column-2 rh-pr-0_5', {
                        'custom-rate-expanded': !isDesktop,
                    })}
                    >
                        <QuestionCompact
                            field={{
                                name: 'customRate',
                                type: 'float',
                                value: rate.value,
                                validators: [ {
                                    fn: value => value > 0 && value <= 20,
                                    message: (
                                        <FormattedMessage
                                            id="mtg-common.MortgageRateSelector.customRateError"
                                            defaultMessage="Please enter valid rate"
                                        />
                                    ),
                                } ],
                                required: false,
                            }}
                            type="percent"
                            labelMessage={(
                                <FormattedMessage
                                    id="mtg-common.MortgageRateSelector.customRateLabel"
                                    defaultMessage="Custom rate"
                                />
                            )}
                            shouldRemoveMargin
                        />
                    </div>
                </Otherwise>
            </Choose>

            <div className="button-container rh-display-flex size-me-fix">
                <PrimaryButton
                    type="button"
                    size="small"
                    className="button"
                    message={(
                        <FormattedMessage
                            id="mtg-common.MortgageRateSelector.selectRateButton"
                            defaultMessage="Select"
                        />
                    )}
                    // don't allow selection of invalid custom rate
                    disabled={!isProviderRate(rate) && (customRateField?.typedValue === null || !customRateField?.isValid)}
                    onClick={() => onSelectRate(isProviderRate(rate) ? rate : { value: customRateField?.typedValue })}
                    data-name="select-rate"
                />
            </div>
        </Container>
    );
}

MortgageRateSelector.propTypes = {
    rate: PropTypes.object.isRequired, // rates API response object
    className: PropTypes.string,
    onSelectRate: PropTypes.func.isRequired,
    size: PropTypes.shape({ width: PropTypes.number }).isRequired,
};

MortgageRateSelector.defaultProps = {
    className: undefined,
};

const MOBILE_WIDTH = 440; // iPhone 14 Pro Max width

const Container = styled.div`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    flex-wrap: no-wrap;
    column-gap: ${Sizes.SPACING.HALF};

    /* flex-basis based on the smallest screen size we support */
    > .column-1 {
        flex: 1 0 5rem;
        align-items: flex-start;
        flex-direction: column;
    }

    > .column-2 {
        flex: 1 1 16rem;

        &.custom-rate-expanded {
            flex: 1 1 25rem;
        }
    }

    > .button-container {
        flex: 1 1 min-content;
        justify-content: flex-end;

        > .button {
            flex: 1 0 min-content;
            
            max-width: 9rem;
        }

        /* size-me detector container causes gap on right side of button */
        &.size-me-fix {
            margin-right: -${Sizes.SPACING.HALF};
        }
    }
`;

export default withSize({ 
    noPlaceholder: false, // prevent jank
})(observer(MortgageRateSelector));